import { resolveDestination } from "routing/to";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { NavigationBarDrawer } from "../NavigationBarDrawer";
import { ButtonsWrapper } from "./AccountDrawer.styles";
import { AccountDrawerProps } from "./AccountDrawer.props";
import { AccountBlock } from "../AccountBlock";
import { AccountMenuButton, AccountMenuLink } from "../AccountMenuButton";
import { Divider } from "../Divider";
import { useLogout, useAccountNavigationConfig, useAuthState } from "../../../hooks";
import { withFeatureVisibility } from "../../../HoC";
import { ShopSwitch } from "../ShopSwitch";

export const AccountDrawer = ({
  onClose,
  onNavigate,
  open,
  shopId,
  userId,
  withShopNavigation = false,
  questionnaireIncomplete = false
}: AccountDrawerProps) => {
  const { t } = useTranslation();

  const { pathname } = useLocation();

  const accountMenuRoutes = useAccountNavigationConfig();

  const { checkPermission } = useAuthState();

  const badgesConfig: Record<string, boolean> = {
    questionnaireIncomplete
  };

  const handleLogout = useLogout();

  const parsedButtons = useMemo(
    () =>
      accountMenuRoutes.map(
        ({ translationKey, path, badgeKey, requiredPermission, featureFlag }) => {
          if (requiredPermission && !checkPermission(requiredPermission)) return null;

          const active = pathname === resolveDestination(path, shopId);
          const badge = badgeKey ? badgesConfig[badgeKey] : undefined;
          const to = resolveDestination(path, shopId);
          if (to === undefined) return null;

          if (featureFlag) {
            const Component = withFeatureVisibility(AccountMenuLink, featureFlag);

            return (
              <Component active={active} key={translationKey} showBadge={badge} to={to}>
                {t(translationKey)}
              </Component>
            );
          }

          return (
            <AccountMenuLink
              active={active}
              key={translationKey}
              onClick={onNavigate}
              showBadge={badge}
              to={to}
            >
              {t(translationKey)}
            </AccountMenuLink>
          );
        }
      ),
    [accountMenuRoutes, badgesConfig, checkPermission, onNavigate, pathname, shopId, t]
  );

  return (
    <NavigationBarDrawer variant="temporary" anchor="right" open={open} onClose={onClose}>
      <ButtonsWrapper>
        <AccountBlock
          userId={userId}
          shopId={withShopNavigation ? shopId?.toString() : undefined}
        />
        <Divider />
        {withShopNavigation && <ShopSwitch />}
        {parsedButtons}
        <Divider />
        <AccountMenuButton
          onClick={handleLogout}
          iconPosition="start"
          icon={<ExitToAppIcon viewBox={"0 0 22 22"} />}
        >
          {t("navigation.accountMenu.logout")}
        </AccountMenuButton>
      </ButtonsWrapper>
    </NavigationBarDrawer>
  );
};
